import React from 'react';

import t from '../../../labels/labelling-utils';
import { makeClassNames } from '../../../utils/styles';
import ErrorComponent from '../../atoms/error/error';
import TeamsList, { ITeamsList } from '../../molecules/teams-list/teams-list';
import Accordion, { IAccordion, IAccordionTitle } from '../../organisms/accordion/accordion';
import ErrorBoundary from '../error-boundary/error-boundary';

export interface ILeagueList extends ITeamsList {
  seasonAndCompId: string;
  seasonLabel?: string;
  name: string;
  id: string;
  logoUrl?: string;
  homepageUrl?: string;
}

interface ILeaguesTrackingData {
  dataAttribute?: string;
  accordion?: string;
  team?: string;
}

type TrackingDataType = 'accordion' | 'team';

export interface ILeagues extends IAccordion {
  leagues: ILeagueList[];
  customTrackingData?: ILeaguesTrackingData;
}

const classNames = () =>
  makeClassNames({
    leagues: 'leagues',
  });

const DEFAULT_TITLE_COLOR = 'blue';
const DEFAULT_TITLE_SIZE = '14--16';
const DEFAULT_TITLE_WEIGHT = '500';

const getErrorComponent = () => <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;

const isValidLeagues = (leagues: ILeagueList[]) => Boolean(leagues && leagues.length);

const getAccordionTitleObject = (competitionName: string, seasonLabel = '', url?: string): IAccordionTitle => ({
  url,
  text: `${competitionName} - ${seasonLabel}`,
  color: DEFAULT_TITLE_COLOR,
  fontSize: DEFAULT_TITLE_SIZE,
  fontWeight: DEFAULT_TITLE_WEIGHT,
});

/**
 * Get the tracking data for Accordion or single Team
 * @param trackingData tracking element
 * @returns tracking data object
 */
const getTrackingData = (
  trackingData: ILeaguesTrackingData | undefined,
  variant: TrackingDataType = 'accordion'
): object =>
  trackingData && trackingData.dataAttribute
    ? {
        [trackingData.dataAttribute]: trackingData[variant],
      }
    : {};

const Leagues = ({
  /* istanbul ignore next: default value on mandatory field */ leagues = [],
  /* istanbul ignore next: default value on mandatory field */ customTrackingData = {},
}: ILeagues) => {
  const cn = classNames();
  const trackingDataAccordion = getTrackingData(customTrackingData);
  const trackingDataTeam = getTrackingData(customTrackingData, 'team');
  return (
    <div className={cn.leagues}>
      <ErrorBoundary hasError={!isValidLeagues(leagues)} errorComponent={getErrorComponent()}>
        {leagues.map((leagueTeams: ILeagueList, key: number) => {
          const { logoUrl: logo, name, seasonLabel, teams, homepageUrl: url, id }: ILeagueList = leagueTeams;
          const accordionTitle: IAccordionTitle = getAccordionTitleObject(name, seasonLabel, url);
          return (
            <Accordion
              key={leagueTeams.id}
              isOpen={key === 0}
              logoUrl={logo}
              title={accordionTitle}
              id={id}
              trackingData={trackingDataAccordion}
              isUpperCase
            >
              <TeamsList teams={teams} trackingData={trackingDataTeam} />
            </Accordion>
          );
        })}
      </ErrorBoundary>
    </div>
  );
};

export default Leagues;
